import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  type Cadence,
  type SchedulableAuditType,
  SCHEDULABLE_AUDIT_TYPES,
  describeCadence,
  resolveCadence,
  isCadenceConfigurable,
  isBrandAxisAuditType,
} from '@traaker/shared';
import { Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { formatDateTime } from '../../i18n/format';

/** i18n key per axis — resolved with `t()` at render, so the FR UI shows the
 *  French axis vocabulary (Image / Autorité / Recommandation). */
export const AXIS_LABEL_KEYS: Record<SchedulableAuditType, string> = {
  website: 'cadenceEditor.axis.website',
  earned_media: 'cadenceEditor.axis.earnedMedia',
  brand_narrative: 'cadenceEditor.axis.narrative',
  brand_authority: 'cadenceEditor.axis.authority',
  brand_referral: 'cadenceEditor.axis.referral',
  // news_watch removed from SCHEDULABLE_AUDIT_TYPES (Studio de Création is a
  // monthly-quota PULL feature, no cadence — 2026-07-05).
};

/** The axes this editor shows and writes. The prompt cadence (the `brand_*` axes) is
 *  set in the Prompt Studio footer only, through `promptCadence` (Prompt Studio G 6b). */
const EDITOR_AXES = SCHEDULABLE_AUDIT_TYPES.filter((a) => !isBrandAxisAuditType(a));

/** The `PATCH /brands/:id/audit-config` body. The draft is seeded from the stored map,
 *  which holds the `brand_*` keys: never send them (the API refuses them with a 400).
 *  `undefined` entries mean "fall through to tier default" and are omitted. */
export function buildCadencePatchBody(
  draft: Partial<Record<SchedulableAuditType, Cadence | undefined>>,
): { cadenceOverride: Partial<Record<SchedulableAuditType, Cadence>> } {
  const cleaned: Partial<Record<SchedulableAuditType, Cadence>> = {};
  for (const axis of EDITOR_AXES) {
    const v = draft[axis];
    if (v !== undefined) cleaned[axis] = v;
  }
  return { cadenceOverride: cleaned };
}

interface Props {
  brandId: string;
  tier: string;
  cadenceOverride: Partial<Record<SchedulableAuditType, Cadence>> | undefined;
  onSaved: () => Promise<void> | void;
}

/**
 * Per-axis cadence editor for one brand.
 *
 * Layout: one row per axis with a toggle (on/off), interval input, and unit
 * select. Tier-default values render as placeholder text when no override is
 * set; toggling off explicitly stores `'off'` (which differs from "use tier
 * default" — see plan doc §0.2 D-2).
 *
 * On save, calls `PATCH /brands/:brandId/audit-config` with the diff.
 */
type NextRun = { axis: SchedulableAuditType; nextRunAt: string | null };

function formatNextRun(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return formatDateTime(d, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function CadenceEditor({ brandId, tier, cadenceOverride, onSaved }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<
    Partial<Record<SchedulableAuditType, Cadence | undefined>>
  >(cadenceOverride || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextRuns, setNextRuns] = useState<Partial<Record<SchedulableAuditType, string | null>>>({});

  useEffect(() => {
    setDraft(cadenceOverride || {});
    setError(null);
  }, [cadenceOverride, brandId]);

  // Next scheduled date per axis — computed server-side from the materialized
  // schedule anchor (no EventBridge call). Re-fetched whenever the brand or its
  // cadence override changes (i.e. after a save).
  useEffect(() => {
    let cancelled = false;
    api
      .get<NextRun[]>(`/brands/${brandId}/next-runs`)
      .then((rows) => {
        if (cancelled) return;
        const map: Partial<Record<SchedulableAuditType, string | null>> = {};
        for (const r of rows) map[r.axis] = r.nextRunAt;
        setNextRuns(map);
      })
      .catch(() => {
        if (!cancelled) setNextRuns({});
      });
    return () => {
      cancelled = true;
    };
  }, [brandId, cadenceOverride]);

  const setAxis = (axis: SchedulableAuditType, c: Cadence | undefined) => {
    setDraft((prev) => ({ ...prev, [axis]: c }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/brands/${brandId}/audit-config`, buildCadencePatchBody(draft));
      await onSaved();
    } catch (err: any) {
      setError(err?.message || t('cadenceEditor.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  // Per axis: a self-serve tier (basic/pro/elite) is LOCKED to the tier default
  // cadence — read-only rows (no controls, no Save). Enterprise/agency configure.
  const lockedAxes = EDITOR_AXES.filter((axis) => !isCadenceConfigurable(tier, axis));
  const editableAxes = EDITOR_AXES.filter((axis) => isCadenceConfigurable(tier, axis));

  // The prompt cadence lives in the Prompt Studio footer. A full navigation (not a
  // router link) so BrandContext's `?brandId=` deep link opens THIS brand's page.
  const promptStudioLine = (
    <Link variant="body2" href={`/brand/narrative?brandId=${encodeURIComponent(brandId)}&studio=footer`}>
      {t('cadenceEditor.promptCadenceInStudio')}
    </Link>
  );

  return (
    <Stack spacing={2}>
      {lockedAxes.length > 0 && (
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <LockOutlinedIcon sx={{ fontSize: 16, color: 'action.active' }} />
            <Typography variant="body2" color="text.secondary">
              {t('cadenceEditor.lockedHelp')}
            </Typography>
          </Box>
          <Stack spacing={1}>
            {lockedAxes.map((axis) => {
              const c = resolveCadence(undefined, tier, axis);
              return (
                <Box key={axis} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight={600}>{t(AXIS_LABEL_KEYS[axis])}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('cadenceEditor.nextRun', { date: formatNextRun(nextRuns[axis] ?? null) })}
                    </Typography>
                    <Chip size="small" variant="outlined" label={describeCadence(c)} />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      )}

      {editableAxes.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('cadenceEditor.intro')}
        </Typography>
      )}

      {editableAxes.map((axis) => {
        const value = draft[axis];
        const tierDefault = resolveCadence(undefined, tier, axis);
        const effective = value !== undefined ? value : tierDefault;
        const isOn = effective !== 'off';
        const editable = value !== undefined ? value : tierDefault;
        const intervalValue =
          editable !== 'off' && typeof editable === 'object' ? editable.interval : 1;
        const unitValue =
          editable !== 'off' && typeof editable === 'object' ? editable.unit : 'days';
        const isOverridden = value !== undefined;

        return (
          <Box
            key={axis}
            sx={{
              display: 'grid',
              gridTemplateColumns: '180px 60px 1fr 130px 140px 32px',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {t(AXIS_LABEL_KEYS[axis])}
            </Typography>

            <Switch
              size="small"
              checked={isOn}
              onChange={(e) =>
                setAxis(
                  axis,
                  e.target.checked ? { interval: intervalValue, unit: unitValue } : 'off',
                )
              }
            />

            <Typography variant="caption" color="text.secondary">
              {isOn
                ? t(isOverridden ? 'cadenceEditor.rowActive' : 'cadenceEditor.rowActiveDefault', {
                    cadence: describeCadence(effective),
                    date: formatNextRun(nextRuns[axis] ?? null),
                  })
                : t('cadenceEditor.disabled')}
            </Typography>

            <TextField
              size="small"
              type="number"
              disabled={!isOn}
              value={intervalValue}
              inputProps={{ min: 1, max: 30 }}
              onChange={(e) => {
                const n = Math.max(1, Math.min(30, parseInt(e.target.value, 10) || 1));
                setAxis(axis, { interval: n, unit: unitValue });
              }}
            />

            <FormControl size="small">
              <Select
                disabled={!isOn}
                value={unitValue}
                onChange={(e) =>
                  setAxis(axis, {
                    interval: intervalValue,
                    unit: e.target.value as 'days' | 'weeks' | 'months',
                  })
                }
              >
                <MenuItem value="days">{t('cadenceEditor.unitDays')}</MenuItem>
                <MenuItem value="weeks">{t('cadenceEditor.unitWeeks')}</MenuItem>
                <MenuItem value="months">{t('cadenceEditor.unitMonths')}</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title={t('cadenceEditor.restoreDefault')}>
              <span>
                <IconButton
                  size="small"
                  disabled={!isOverridden}
                  onClick={() => setAxis(axis, undefined)}
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      })}

      {promptStudioLine}

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {editableAxes.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" disabled={saving} onClick={handleSave}>
            {saving ? t('cadenceEditor.saving') : t('cadenceEditor.save')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
